<template>
  <div>
    <form action>
      <div class="row">
        <div class="col-lg-8">
          <label for="autocomplete">{{ location }}</label>
          <input type="text" id="autocomplete" ref="autocomplete" :placeholder="placeholder" />
        </div>

        <div class="col-lg-2">
          <label for="distance">{{ distance }}</label>
          <select name="distance" id="distance" v-model="radius">
            <option :value="kmOrMiNumber(10)">10{{ kmOrMiSymbol }}</option>
            <option :value="kmOrMiNumber(25)">25{{ kmOrMiSymbol }}</option>
            <option :value="kmOrMiNumber(50)">50{{ kmOrMiSymbol }}</option>
            <option :value="kmOrMiNumber(100)">100{{ kmOrMiSymbol }}</option>
          </select>
        </div>

        <div class="col-lg-2">
          <label for class="invisible">Button</label>
          <button
            @click.prevent="getData"
            class="btn btn-primary btn-hover"
            id="store_locator_submit"
            :disabled="!lat || !lng"
          >{{ search }}</button>
        </div>
      </div>
    </form>

    <transition>
      <div v-show="stores.length">
        <p class="result-desc">
          <span>{{ filteredStores.length }}</span>
          {{ stores_within }}
          <span>{{ radius }}{{ kmOrMiSymbol }}</span>
        </p>

        <div class="filters-container">
          <h4
            @click="toggleFilters"
            :style="{ backgroundImage: `url('${ ~showFilters ? filterIcon : closeIcon }')` }"
          >{{ refine_your_search }}</h4>
          <transition>
            <div class="filters" v-if="showFilters">
              <h6>{{ filter_by_stock_type }}</h6>
              <div class="row">
                <div class="col-6 col-md-4" v-for="(stockType, index) in stockTypes" :key="index">
                  <div class="type">
                    <label>
                      <input type="checkbox" name id v-model="stockFilters" :value="stockType.id" />
                      <img class="icon" :src="stockType.icon" />
                      <span>{{ storeProducts[stockType.title] ? storeProducts[stockType.title] : stockType.title }}</span>
                    </label>
                  </div>
                </div>
              </div>
              <hr />
              <h6>{{ filter_by_services }}</h6>
              <div class="row">
                <div
                  class="col-6 col-md-4"
                  v-for="(serviceType, index) in serviceTypes"
                  :key="index"
                >
                  <label>
                    <input type="checkbox" name id v-model="serviceFilters" :value="serviceType.id" />
                    <img class="icon" :src="serviceType.icon" />
                    <span>{{ storeServices[serviceType.title] ? storeServices[serviceType.title] : serviceType.title }}</span>
                  </label>
                </div>
              </div>
              <a href class="btn btn-primary btn-hover">{{ refine }}</a>
            </div>
          </transition>
        </div>

        <div class="row row-reverse">
          <div class="col-lg-6">
            <div class="map" ref="map"></div>
          </div>

          <div class="col-lg-6">
            <div class="store-list">
              <div class="store" v-for="(store, index) in filteredStores" :key="index">
                <div class="top" @click="toggleStoreDetail(index)">
                  <div class="pin">
                    <span>{{ index + 1 }}</span>
                    <img loading="lazy" src="/cdn/images/temp/map-marker--list.png" alt="map marker" />
                  </div>
                  <div class="address">
                    <h2>{{ store.title }}</h2>
                    <p>
                      {{ store.address }}
                      <br />
                      {{ store.suburb }}, {{ store.state }}
                      {{ store.postcode }}
                    </p>
                  </div>
                  <div>
                    <img
                      loading="lazy"
                      src="/cdn/images/temp/select-arrow.svg"
                      alt
                      class="select-arrow"
                    />
                  </div>
                </div>
                <div class="detail" :id="`detail-${index}`">
                  <div class="phone">
                    <img loading="lazy" src="/cdn/images/temp/phone.svg" />
                    {{ store.phone || no_phone_number }}
                  </div>

                  <div v-if="getStockTypes(store).length > 0">
                    <h3>{{ stocks }}</h3>
                    <div class="row">
                      <div
                        v-for="(stockType,
                                                index) in getStockTypes(store)"
                        :key="index"
                        class="col-6 col-md-4"
                      >
                        <img class="icon" :src="stockType.icon" />
                        {{ storeProducts[stockType.title] ? storeProducts[stockType.title] : stockType.title }}
                      </div>
                    </div>
                  </div>

                  <div v-if="getServiceTypes(store).length > 0">
                    <h3>{{ services }}</h3>
                    <div class="row">
                      <div
                        v-for="(serviceType,
                                                index) in getServiceTypes(store)"
                        :key="index"
                        class="col-6 col-md-4"
                      >
                        <img class="icon" :src="serviceType.icon" />
                        {{ storeServices[serviceType.title] ? storeServices[serviceType.title] : serviceType.title}}
                      </div>
                    </div>
                  </div>

                  <a
                    :href="`/dealers/${store.slug}`"
                    class="btn btn-hover btn-primary learn_more_button"
                  >{{ learn_more }}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import gmapsInit from "../utils/gmaps.js";

export default {
  name: "store-locator",
  props: {
    sskey: {
      type: String,
      default: "dealerMode"
    },
    imperial_system: {
      type: String,
      default: "0"
    },
    store_products: {
      type: String
    },
    store_services: {
      type: String
    },
    latitude: {
      type: String
    },
    longitude: {
      type: String
    },
    formatted_address: {
      type: String
    },
    placeholder: {
      type: String,
      required: true,
      default: "Search by suburb or postcode"
    },
    search: {
      type: String,
      required: true,
      default: "Search"
    },
    refine: {
      type: String,
      required: true,
      default: "Refine"
    },
    distance: {
      type: String,
      required: true,
      default: "Distance"
    },
    location: {
      type: String,
      required: true,
      default: "Location"
    },
    stores_within: {
      type: String,
      required: true,
      default: "stores within"
    },
    refine_your_search: {
      type: String,
      required: true,
      default: "Refine your search"
    },
    no_phone_number: {
      type: String,
      required: true,
      default: "No Phone Number"
    },
    learn_more: {
      type: String,
      required: true,
      default: "Learn More"
    },
    stocks: {
      type: String,
      required: true,
      default: "Stocks"
    },
    services: {
      type: String,
      required: true,
      default: "Services"
    },
    filter_by_stock_type: {
      type: String,
      required: true,
      default: "Filter by Stock Type"
    },
    filter_by_services: {
      type: String,
      required: true,
      default: "Filter by Services"
    }
  },
  data() {
    return {
      baseUrl: "api/search/store/",
      radius: this.kmOrMiNumber(10),
      limit: 50,
      stores: [],
      serviceTypes: [],
      serviceFilters: [],
      stockTypes: [],
      stockFilters: [],
      lat: null,
      lng: null,
      postcode: null,
      showFilters: true,
      filterIcon: "../images/temp/filter.svg",
      closeIcon: "../images/temp/close-2.svg",
      sessionStorageSet: false
    };
  },
  async mounted() {
    let len = sessionStorage.getItem(this.sskey)
      ? sessionStorage.getItem(this.sskey).length
      : 0;
    this.sessionStorageSet = len > 0 ? true : false;

    // create google
    window.google = await gmapsInit();

    // set initial lat and lng to props
    this.lat = this.latitude;
    this.lng = this.longitude;

    if (this.formatted_address) {
      // console.log('this.$refs.autocomplete', this.$refs.autocomplete)

      this.$refs.autocomplete.value = this.formatted_address;
    }

    if (this.lat && this.lng) {
      this.getData();
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      this.$refs.autocomplete,
      {
        componentRestrictions: {
          country: window.culture.split("-")[1]
        },
        types: ["(regions)"]
      }
    );

    autocomplete.addListener("place_changed", () => {
      this.lat = null;
      this.lng = null;

      let place = autocomplete.getPlace();

      // get postcode
      this.postcode = this.extractPostcode(place.address_components);

      // set lat and long
      this.lat = place.geometry.location.lat();
      this.lng = place.geometry.location.lng();
    });
  },
  computed: {
    isMetricSystem() {
      return this.imperial_system !== "1" ? true : false;
    },
    kmOrMiSymbol() {
      return this.isMetricSystem ? "km" : "mi";
    },
    url() {
      if (this.sessionStorageSet === true) {
        return `${this.baseUrl}${this.lat}/${this.lng}/${this.radius}/${this.limit}/true`;
      }
      return `${this.baseUrl}${this.lat}/${this.lng}/${this.radius}/${this.limit}`;
    },
    storeProducts() {
      return JSON.parse(this.store_products);
    },
    storeServices() {
      return JSON.parse(this.store_services);
    },
    filteredStores() {
      let stores = this.stores;

      // If no filters, return all stores
      if (this.serviceFilters.length == 0 && this.stockFilters.length == 0) {
        return stores;
      }

      let filtered = [];
      stores.forEach(store => {
        // check service filters
        if (store.store_service_ids != null && this.serviceFilters.length > 0) {
          let serviceIds = store.store_service_ids
            .split(",")
            .map(id => parseInt(id));

          this.serviceFilters.forEach(serviceId => {
            if (serviceIds.includes(serviceId)) {
              filtered.push(store);
            }
          });
        }

        // check stock filters
        if (store.store_stock_ids != null && this.stockFilters.length > 0) {
          let stockIds = store.store_stock_ids
            .split(",")
            .map(id => parseInt(id));

          this.stockFilters.forEach(stockId => {
            if (stockIds.includes(stockId)) {
              filtered.push(store);
            }
          });
        }
      });

      return [...new Set(filtered)];
    }
  },
  watch: {
    filteredStores() {
      const map = new window.google.maps.Map(this.$refs.map, {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true
      });
      const bounds = new window.google.maps.LatLngBounds();

      let markers = [];
      let windowOpen;
      this.filteredStores.forEach((store, index) => {
        let { latitude: lat, longitude: lng, title } = store;
        let marker = new window.google.maps.Marker({
          position: { lat, lng },
          icon: {
            url: "/cdn/images/temp/map-marker--map.png",
            scaledSize: window.google.maps.Size(10, 20)
          },
          label: {
            text: `${index + 1}`,
            color: "white"
          },
          title,
          map
        });

        markers.push(marker);
        bounds.extend(marker.getPosition());
        map.fitBounds(bounds);

        let infowindow = new window.google.maps.InfoWindow({
          content: `
                      <h6>${store.title}</h6>
                      <p>${store.address}<br />
                      ${store.suburb}<br />
                      ${store.state} ${store.postcode}</p>
                      <p>Phone: ${store.phone}</p>
                    `
        });

        marker.addListener("click", function() {
          if (windowOpen) {
            windowOpen.close();
          }
          infowindow.open(map, marker);

          windowOpen = infowindow;
        });
      });
    }
  },
  methods: {
    async getData() {
      let response = await axios.get(this.url);

      this.stores = response.data.data.stores;
      this.serviceTypes = response.data.data.service_types;
      this.stockTypes = response.data.data.stock_types;
    },
    extractPostcode(address) {
      let postcode;
      address.forEach(part => {
        if (part.types[0] == "postal_code") {
          postcode = part.long_name;
        }
      });

      return postcode;
    },
    getStockTypes(store) {
      let list = [];
      // check if there are ids
      if (store.store_stock_ids !== null) {
        let stockIds = store.store_stock_ids.split(",");

        // now match to the stock list to get name and icon
        stockIds.forEach(id => {
          this.stockTypes.forEach(type => {
            if (id == type.id) {
              list.push(type);
            }
          });
        });
      }

      return list;
    },
    getServiceTypes(store) {
      let list = [];
      // check if there are ids
      if (store.store_service_ids !== null) {
        let serviceIds = store.store_service_ids.split(",");

        // now match to the service list to get name and icon
        serviceIds.forEach(id => {
          this.serviceTypes.forEach(type => {
            if (id == type.id) {
              list.push(type);
            }
          });
        });
      }

      return list;
    },
    toggleStoreDetail(id) {
      document.getElementById(`detail-${id}`).classList.toggle("open");
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    kmOrMiNumber(n) {
      return this.isMetricSystem ? n : Math.round(n * 1.6);
    }
  }
};
</script>
